import React from "react";
import { motion } from "framer-motion";

// Abstract seed inside orbit rings — universal growth metaphor
export function OrbitSeed({ size = 400 }) {
  return (
    <svg viewBox="0 0 400 400" width={size} height={size} className="max-w-full h-auto" data-testid="illus-orbit-seed">
      <defs>
        <linearGradient id="seedGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E8552B" />
          <stop offset="100%" stopColor="#F4A72C" />
        </linearGradient>
      </defs>
      {[180, 140, 100].map((r, i) => (
        <motion.circle
          key={r} cx="200" cy="200" r={r}
          fill="none" stroke={["#E8552B", "#F4A72C", "#7BA098"][i]} strokeWidth="2" opacity={0.75 - i * 0.15}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.75 - i * 0.15 }}
          transition={{ duration: 2, delay: i * 0.3 }}
        />
      ))}
      <motion.ellipse cx="200" cy="200" rx="42" ry="60" fill="url(#seedGrad)"
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.6, ease: "easeOut" }} />
      <motion.circle cx="380" cy="200" r="4" fill="#E8552B"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.8 }} />
      <motion.circle cx="60" cy="140" r="3" fill="#7BA098"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2 }} />
      <motion.circle cx="300" cy="60" r="2" fill="#F4A72C"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2.2 }} />
    </svg>
  );
}

// Moon phases — journey through stages
export function MoonPhases({ size = 320 }) {
  const phases = [0.1, 0.3, 0.55, 0.8, 1];
  return (
    <svg viewBox="0 0 500 100" width={size} height={size / 5} className="max-w-full" data-testid="illus-moon-phases">
      {phases.map((p, i) => (
        <g key={i} transform={`translate(${i * 100 + 50}, 50)`}>
          <motion.circle cx="0" cy="0" r="30" fill="#F5E7CE"
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.15 }} />
          <motion.path d={`M 0 -30 A 30 30 0 0 1 0 30 A ${30 * (1 - p * 2)} 30 0 0 ${p > 0.5 ? 1 : 0} 0 -30 Z`}
            fill="#1B1533" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: i * 0.15 + 0.2 }} />
        </g>
      ))}
    </svg>
  );
}

// Intersecting curves — connection & family
export function CurveWeave({ size = 400 }) {
  return (
    <svg viewBox="0 0 400 400" width={size} height={size} className="max-w-full" data-testid="illus-curve-weave">
      {[0, 60, 120, 180].map((r, i) => (
        <motion.path
          key={r} d={`M 40 ${200 - r + 100} Q 200 ${100 - r} 360 ${200 - r + 100}`}
          fill="none" stroke={["#E8552B", "#F4A72C", "#7BA098", "#1B1533"][i]}
          strokeWidth="1.5" opacity={0.7 - i * 0.1}
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
          viewport={{ once: true }} transition={{ duration: 2, delay: i * 0.2 }}
        />
      ))}
    </svg>
  );
}

// Growth rings — timeline metaphor
export function GrowthRings({ size = 320 }) {
  return (
    <svg viewBox="0 0 300 300" width={size} height={size} className="max-w-full" data-testid="illus-growth-rings">
      {[130, 105, 80, 55, 30].map((r, i) => (
        <motion.circle
          key={r} cx="150" cy="150" r={r} fill="none"
          stroke={i % 2 ? "#E8552B" : "#F4A72C"} strokeWidth="1.5" opacity={0.6}
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
          viewport={{ once: true }} transition={{ duration: 1.5, delay: i * 0.2 }}
        />
      ))}
      <motion.circle cx="150" cy="150" r="8" fill="#1B1533"
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.2 }} />
    </svg>
  );
}
